import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";

const RangeDetails = () => {
  const { rangeId } = useParams();
  const location = useLocation();
  const rangeName = location.state?.rangeName;
  const Api = process.env.REACT_APP_API;
  const navigate = useNavigate();

  const [jfmcTypes, setJfmcTypes] = useState([]);
  const [activeJfmc, setActiveJfmc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchVfcs = async () => {
      try {
        const config = Authorization();
        const res = await axios.get(
          `${Api}/api/review-dashboard/review-dashboard/range/${rangeId}`,
          config
        );
        const payload = res.data;
        const jfmcArray = Array.isArray(payload) ? payload : [payload];
        setJfmcTypes(jfmcArray);
        setActiveJfmc(jfmcArray[0]);
      } catch (err) {
        console.error(err);
        setError("Unable to load VFCs");
      } finally {
        setLoading(false);
      }
    };
    fetchVfcs();
  }, [Api, rangeId]);

  if (loading) return <Preloader loading={true} />;
  if (error) return <p className="text-danger">{error}</p>;
  if (!activeJfmc) return null;

  const { vfcs = [], totals = {} } = activeJfmc;

  const countBadge = (v, type) => {
    const zero = v === 0;
    const s = type === "total"
      ? { bg: "#E8EDF5", cl: "#3A5080", br: "#C5D0E6" }
      : type === "done"
        ? zero ? { bg: "#F1F4F9", cl: "#9BA8BF", br: "#DDE3EE" } : { bg: "#D1EFD8", cl: "#14532D", br: "#A3D9B0" }
        : zero ? { bg: "#D1EFD8", cl: "#14532D", br: "#A3D9B0" } : { bg: "#FFE0D0", cl: "#7C2200", br: "#FFBFA0" };
    return (
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 36, padding: "3px 10px", borderRadius: "50rem", fontSize: 12, fontWeight: 700, background: s.bg, color: s.cl, border: `1px solid ${s.br}` }}>
        {v}
      </span>
    );
  };

  const dateBadge = (done, date) => {
    if (done && date) {
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: "50rem", fontSize: 11, fontWeight: 600, background: "#D1EFD8", color: "#14532D", border: "1px solid #A3D9B0", whiteSpace: "nowrap" }}>
          <i className="mdi mdi-check-circle" style={{ fontSize: 12 }} />
          {new Date(date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      );
    }
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: "50rem", fontSize: 11, fontWeight: 600, background: "#FFE0D0", color: "#7C2200", border: "1px solid #FFBFA0", whiteSpace: "nowrap" }}>
        <i className="mdi mdi-close-circle" style={{ fontSize: 12 }} />
        Not Done
      </span>
    );
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div className="rd-page-header">
        <div className="rd-header-left">
          <button
            className="btn btn-sm btn-outline-secondary me-3"
            style={{ borderRadius: 8, padding: "6px 14px", fontWeight: 600, fontSize: 12 }}
            onClick={() => navigate(-1)}
          >
            <i className="mdi mdi-arrow-left me-1" /> Back
          </button>
          <span className="page-title-icon">
            <i className="mdi mdi-forest" />
          </span>
          <div>
            <h3 className="rd-page-title">{rangeName || "Range"}</h3>
            <p className="rd-page-sub">VFC-wise compliance details</p>
          </div>
        </div>
        <div className="jfmc-switch">
          {jfmcTypes.map((j) => (
            <button
              key={j.jfmcTypeId}
              className={`btn ${activeJfmc.jfmcTypeId === j.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
              onClick={() => setActiveJfmc(j)}
            >
              {j.jfmcTypeName}
            </button>
          ))}
        </div>
      </div>

      {/* ── JFMC Section ── */}
      <div className="jfmc-section">
        <h3 className="jfmc-title">{activeJfmc.jfmcTypeName} — Compliance Overview</h3>

        {/* ── KPI Cards ── */}
        <div key={activeJfmc.jfmcTypeId} style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 16 }}>
          <div className="stat-card card-green stat-centered">
            <div className="stat-icon-big icon-green"><i className="fas fa-users-cog" /></div>
            <div className="stat-title">EC Meetings</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.ecDone}</div><div className="mini-label">Held</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.ecNotDone}</div><div className="mini-label">Not Held</div></div>
            </div>
          </div>
          <div className="stat-card card-blue stat-centered">
            <div className="stat-icon-big icon-blue"><i className="fas fa-users" /></div>
            <div className="stat-title">General Body</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.gbDone}</div><div className="mini-label">Held</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.gbNotDone}</div><div className="mini-label">Not Held</div></div>
            </div>
          </div>
          <div className="stat-card card-orange stat-centered">
            <div className="stat-icon-big icon-orange"><i className="fas fa-file-signature" /></div>
            <div className="stat-title">Audit</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.auditDone}</div><div className="mini-label">Audited</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.auditNotDone}</div><div className="mini-label">Pending</div></div>
            </div>
          </div>
          <div className="stat-card card-purple stat-centered">
            <div className="stat-icon-big icon-purple"><i className="fas fa-sync-alt" /></div>
            <div className="stat-title">Renewal</div>
            <div className="stat-box-grid">
              <div className="stat-mini-box"><div className="mini-value">{totals.renewalDone}</div><div className="mini-label">Renewed</div></div>
              <div className="stat-mini-box"><div className="mini-value">{totals.renewalNotDone}</div><div className="mini-label">Pending</div></div>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ marginTop: 24, background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7" }}>
            <span style={{ width: 36, height: 36, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 16, flexShrink: 0 }}>
              <i className="mdi mdi-table-large" />
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1A202C" }}>{activeJfmc.jfmcTypeName} — VFC-wise Details</div>
              <div style={{ fontSize: 11, color: "#A0AEC0", fontWeight: 500, marginTop: 2 }}>Activity dates per VFC</div>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#2E7D32" }}>
                  <th colSpan="2" style={{ background: "#1B5E20", color: "rgba(255,255,255,0.7)", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.6px", padding: "11px 16px", fontWeight: 700, textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.12)" }} />
                  {[
                    { label: "EC Meetings", icon: "fas fa-users-cog" },
                    { label: "General Body", icon: "fas fa-users" },
                    { label: "Audit", icon: "fas fa-file-signature" },
                    { label: "Renewal", icon: "fas fa-sync-alt" },
                  ].map(({ label, icon }, i) => (
                    <th key={label} colSpan="1" style={{ background: "#2E7D32", color: "#ffffff", textAlign: "center", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.5px", padding: "11px 14px", borderRight: i < 3 ? "1px solid rgba(255,255,255,0.15)" : "none" }}>
                      <i className={icon} style={{ marginRight: 6, opacity: 0.85 }} />{label}
                    </th>
                  ))}
                </tr>
                <tr style={{ background: "#E8F5E9" }}>
                  {["VFC Name", "Last Submitted"].map((h, i) => (
                    <th key={h} style={{ background: "#E8F5E9", color: "#1B5E20", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.6px", padding: "9px 16px", fontWeight: 700, whiteSpace: "nowrap", textAlign: "left", borderRight: i === 1 ? "1px solid #C8E6C9" : "none", borderBottom: "2px solid #A5D6A7" }}>
                      {h}
                    </th>
                  ))}
                  {["Date / Status", "Date / Status", "Date / Status", "Date / Status"].map((h, i) => (
                    <th key={i} style={{ background: "#E8F5E9", color: "#1B5E20", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", padding: "9px 12px", fontWeight: 700, textAlign: "center", whiteSpace: "nowrap", borderRight: i < 3 ? "1px solid #C8E6C9" : "none", borderBottom: "2px solid #A5D6A7" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vfcs
                  .sort((a, b) => a.vfcName.localeCompare(b.vfcName))
                  .map((vfc, rowIdx) => {
                    const rowBg = rowIdx % 2 === 0 ? "#ffffff" : "#F8FAFD";
                    const tdBase = { padding: "10px 14px", borderBottom: "1px solid #EDF2F7", background: rowBg, verticalAlign: "middle" };
                    return (
                      <tr key={vfc._id}
                        onMouseEnter={e => [...e.currentTarget.cells].forEach(td => td.style.background = "#EEF3FF")}
                        onMouseLeave={e => [...e.currentTarget.cells].forEach(td => td.style.background = rowBg)}
                      >
                        <td style={{ ...tdBase, padding: "10px 16px", fontWeight: 600, color: "#2D3748", whiteSpace: "nowrap" }}>
                          <i className="mdi mdi-home-group" style={{ marginRight: 5, color: "#81C784", fontSize: 13 }} />{vfc.vfcName}
                        </td>
                        <td style={{ ...tdBase, padding: "10px 16px", color: "#718096", fontSize: 12, whiteSpace: "nowrap", borderRight: "1px solid #E2E8F0" }}>
                          {vfc.lastSubmittedAt ? new Date(vfc.lastSubmittedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : <span style={{ color: "#CBD5E0", fontStyle: "italic", fontSize: 11 }}>N/A</span>}
                        </td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "1px solid #E2E8F0" }}>{dateBadge(vfc.ecDone, vfc.ecDate)}</td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "1px solid #E2E8F0" }}>{dateBadge(vfc.gbDone, vfc.gbDate)}</td>
                        <td style={{ ...tdBase, textAlign: "center", borderRight: "1px solid #E2E8F0" }}>{dateBadge(vfc.auditDone, vfc.auditDate)}</td>
                        <td style={{ ...tdBase, textAlign: "center" }}>{dateBadge(vfc.renewalDone, vfc.renewalDate)}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeDetails;
